<script lang="ts" setup>
import { computed, ref, onMounted } from 'vue'
import { isClient } from '@/composables/useSSR'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartData,
  type ChartOptions,
  type TooltipItem
} from 'chart.js'
import { Line, Bar } from 'vue-chartjs'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler)

export interface ChartDataset {
  label: string
  data: number[]
  borderColor?: string
  backgroundColor?: string
  fill?: boolean
  tension?: number
  borderWidth?: number
  pointRadius?: number
}

const props = withDefaults(
  defineProps<{
    type: 'line' | 'bar'
    labels: string[]
    datasets: ChartDataset[]
    height?: number
    showLegend?: boolean
    formatValue?: (value: number) => string
    yMax?: number
  }>(),
  {
    height: 200,
    showLegend: true,
    formatValue: undefined,
    yMax: undefined
  }
)

const mounted = ref(false)
onMounted(() => {
  mounted.value = true
})

const chartData = computed<ChartData<'line' | 'bar'>>(() => ({
  labels: props.labels,
  datasets: props.datasets.map((ds) => ({
    label: ds.label,
    data: ds.data,
    borderColor: ds.borderColor ?? '#64b5f6',
    backgroundColor: ds.backgroundColor ?? 'rgba(100, 181, 246, 0.15)',
    fill: ds.fill ?? false,
    tension: ds.tension ?? 0.3,
    borderWidth: ds.borderWidth ?? 2,
    pointRadius: ds.pointRadius ?? 0,
    pointHoverRadius: 4
  }))
}))

const chartOptions = computed<ChartOptions<'line' | 'bar'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index' as const,
    intersect: false
  },
  plugins: {
    legend: {
      display: props.showLegend && props.datasets.length > 1,
      position: 'top' as const,
      labels: {
        color: 'rgba(200, 218, 255, 0.6)',
        boxWidth: 12,
        padding: 16,
        font: { size: 11 }
      }
    },
    tooltip: {
      backgroundColor: 'rgba(15, 18, 30, 0.95)',
      borderColor: 'rgba(120, 163, 255, 0.2)',
      borderWidth: 1,
      titleColor: 'rgba(200, 218, 255, 0.6)',
      bodyColor: 'rgba(255, 255, 255, 0.9)',
      titleFont: { size: 11 },
      bodyFont: { size: 12, weight: 'bold' as const },
      padding: 10,
      cornerRadius: 6,
      callbacks: props.formatValue
        ? {
            label: (ctx: TooltipItem<'line' | 'bar'>) =>
              `${ctx.dataset.label ?? ''}: ${props.formatValue!(ctx.parsed.y ?? 0)}`
          }
        : {}
    }
  },
  scales: {
    x: {
      grid: {
        color: 'rgba(120, 163, 255, 0.06)',
        drawTicks: false
      },
      ticks: {
        color: 'rgba(200, 218, 255, 0.4)',
        font: { size: 10 },
        maxRotation: 0,
        autoSkipPadding: 20
      },
      border: { display: false }
    },
    y: {
      beginAtZero: true,
      max: props.yMax,
      grid: {
        color: 'rgba(120, 163, 255, 0.06)',
        drawTicks: false
      },
      ticks: {
        color: 'rgba(200, 218, 255, 0.4)',
        font: { size: 10 },
        padding: 8,
        callback: props.formatValue
          ? (value: number | string) => props.formatValue!(Number(value))
          : undefined
      },
      border: { display: false }
    }
  }
}))
</script>

<template>
  <div class="base-chart" :style="{ height: height + 'px' }">
    <template v-if="isClient && mounted">
      <Line v-if="type === 'line'" :data="(chartData as any)" :options="(chartOptions as any)" />
      <Bar v-else :data="(chartData as any)" :options="(chartOptions as any)" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.base-chart {
  position: relative;
  width: 100%;
}

.v-theme--lightTheme .base-chart {
  :deep(canvas) {
    filter: none;
  }
}
</style>
